import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, Eye, EyeOff, Music2 } from "lucide-react";
import { toast } from "sonner";

export function SinglesPanel() {
  const [singles, setSingles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/singles");
      setSingles(r.data.singles || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/singles/refresh");
      if (r.data.ok) {
        toast.success(`Synced from Apple — ${r.data.added} new, ${r.data.updated} updated (${r.data.total} total).`);
        await load();
      } else {
        toast.error(`Sync failed: ${r.data.error || "unknown error"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRefreshing(false);
    }
  };

  const toggle = async (s) => {
    const next = !(s.visible !== false);
    try {
      await api.patch(`/admin/singles/${s.id}`, { visible: next });
      setSingles((prev) => prev.map((x) => (x.id === s.id ? { ...x, visible: next } : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visibleCount = singles.filter((s) => s.visible !== false).length;

  return (
    <div data-testid="admin-singles-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-white/70">
            {singles.length} singles · {visibleCount} shown publicly
          </p>
          <p className="text-xs text-white/40 mt-1">
            Auto-synced nightly from Apple Music. Hidden singles stay out of the site & schema.
          </p>
        </div>
        <Button
          onClick={refresh}
          disabled={refreshing}
          className="btn-gold rounded-none text-xs inline-flex items-center gap-2"
          data-testid="singles-refresh-btn"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Syncing…" : "Refresh from Apple"}
        </Button>
      </div>

      {loading ? (
        <p className="text-white/40 italic">Loading…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {singles.map((s) => {
            const shown = s.visible !== false;
            return (
              <div
                key={s.id}
                className={`surface p-3 flex items-center gap-3 ${shown ? "" : "opacity-45"}`}
                data-testid="admin-single-row"
              >
                {s.art ? (
                  <img src={s.art} alt="" className="w-12 h-12 rounded object-cover ring-1 ring-white/10 shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center shrink-0">
                    <Music2 size={16} className="text-white/30" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white/85 truncate">{s.title}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/35 mt-0.5">{s.year}</p>
                </div>
                <button
                  onClick={() => toggle(s)}
                  title={shown ? "Hide from site" : "Show on site"}
                  className="shrink-0 text-white/50 hover:text-[color:var(--gold)] transition-colors"
                  data-testid="single-toggle-visible"
                >
                  {shown ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
